import { z } from 'zod';

/**
 * O veredito de uma máquina depois de aplicar uma versão.
 *
 * É o que o portão do canário lê para decidir se a frota inteira avança. Por
 * isso mora aqui, e não dentro do daemon: o daemon produz, a API consome, e
 * duas definições da mesma palavra divergiriam em silêncio — que é exatamente
 * o defeito que este arquivo existe para não repetir.
 *
 * ## Três valores, nunca dois
 *
 * `VERDE` libera. Qualquer outro veredito segura. E **`null` é o terceiro
 * valor**: "não deu para medir". Ele não é verde nem vermelho, e a diferença é
 * material — tratar "não medi" como "está quebrado" travaria a frota para
 * sempre numa máquina sem sessão nenhuma; tratar como "está bom" liberaria a
 * frota por ausência de má notícia, que é a forma mais fácil de confiar numa
 * proteção que não existe.
 *
 * ## Falha vira VERMELHO, nunca ausência
 *
 * Um veredito que não sai não pode virar um verde por omissão. Quem coleta
 * envolve tudo em try/catch e devolve `VERMELHO_CAPACIDADE` com a nota — nunca
 * deixa a exceção virar silêncio.
 */
export const SaudeSchema = z.enum(['VERDE', 'AMARELO_CAPACIDADE', 'VERMELHO_CAPACIDADE']);
export type Saude = z.infer<typeof SaudeSchema>;

/**
 * Os vereditos que liberam a frota. Fora desta lista, ninguém avança.
 *
 * `AMARELO_CAPACIDADE` fica de fora de propósito: capacidade reprovada é botão
 * apagado na tela de alguém. Liberar a frota nesse estado espalha a quebra em
 * vez de contê-la.
 */
export const VEREDITOS_QUE_LIBERAM: readonly Saude[] = ['VERDE'];

export const VeredictoSchema = z.object({
  /** `null` = não deu para medir. Ver o comentário do topo. */
  saude: SaudeSchema.nullable(),
  /** Sessões vivas depois de assentar. `null` = não deu para contar. */
  sessoes: z.number().int().nullable(),
  /** Por que este veredito, em português. Vai para o painel e para o log. */
  nota: z.string().max(300).optional(),
  /** Capacidades reprovadas, quando houver. */
  caidas: z.array(z.string().max(60)).optional(),
});
export type Veredicto = z.infer<typeof VeredictoSchema>;

/** O que a máquina observou de si mesma. Tudo que é `null` é "não medi". */
export interface Observacoes {
  /** A versão que o hub mandou aplicar. */
  readonly versaoEsperada: string;
  /** A versão que está rodando DE VERDADE — lida do processo, não de arquivo. */
  readonly versaoObservada: string | null;
  /** `/api/saude` respondeu 200? `null` quando esta máquina não serve a API. */
  readonly apiViva: boolean | null;
  /** O painel serve um bundle que volta como javascript? `null` se não serve painel. */
  readonly painelIntegro: boolean | null;
  /** Capacidades reprovadas pela sonda. `null` = a sonda não rodou. */
  readonly capacidadesReprovadas: readonly string[] | null;
  /** Sessões ainda reabrindo depois da espera de assentamento. */
  readonly sessoesReabrindo: number;
  /** Sessões vivas depois de assentar. `null` = não deu para contar. */
  readonly sessoesVivas: number | null;
  /**
   * Conversas que estavam vivas quando o daemon caiu e deviam ter voltado
   * sozinhas. É a garantia que o restart automático existe para preservar.
   */
  readonly sessoesQueDeviamVoltar: number;
  /** Quantas de fato voltaram. */
  readonly sessoesQueVoltaram: number;
  /** Memória livre na máquina no momento da medição. `null` = não deu para ler. */
  readonly memoriaLivreMb: number | null;
  /** O piso abaixo do qual esta máquina recusa abrir sessão. */
  readonly memoriaMinimaMb: number;
}

/** Compara versões ignorando o `v` da tag: o daemon diz `0.4.12`, a tag é `v0.4.12`. */
function mesmaVersao(a: string, b: string): boolean {
  return a.replace(/^v/i, '') === b.replace(/^v/i, '');
}

/**
 * Observações viram veredito.
 *
 * Função pura, e a ordem das perguntas é o desenho: primeiro o que é falha
 * definitiva, depois a degradação medida, depois o que não deu para medir, e
 * só então o verde. Inverter isso faria uma máquina com capacidade caída
 * responder "não sei" e passar por um portão que devia segurá-la.
 */
export function traduzirVeredicto(o: Observacoes): Veredicto {
  const sessoes = o.sessoesVivas;

  // 1. Falhas definitivas.
  if (o.versaoObservada === null) {
    return { saude: 'VERMELHO_CAPACIDADE', sessoes, nota: 'nao consegui ler a versao em execucao' };
  }

  // A própria máquina recusa dizer-se saudável numa versão que não é a pedida.
  // O portão do hub confere isto de novo, e a repetição é de propósito: aqui o
  // motivo sai com as duas versões, que é o que faz o log servir para alguma
  // coisa às três da manhã.
  if (!mesmaVersao(o.versaoObservada, o.versaoEsperada)) {
    return {
      saude: 'VERMELHO_CAPACIDADE',
      sessoes,
      nota: `esta em ${o.versaoObservada}, esperava ${o.versaoEsperada}`,
    };
  }

  if (o.apiViva === false) {
    return { saude: 'VERMELHO_CAPACIDADE', sessoes, nota: 'a API nao respondeu' };
  }

  if (o.painelIntegro === false) {
    return { saude: 'VERMELHO_CAPACIDADE', sessoes, nota: 'o painel nao serve o bundle que anuncia' };
  }

  // 2. Degradação medida. É notícia ruim de verdade, não ausência de notícia.
  if (o.capacidadesReprovadas !== null && o.capacidadesReprovadas.length > 0) {
    return {
      saude: 'AMARELO_CAPACIDADE',
      sessoes,
      nota: `capacidade reprovada: ${o.capacidadesReprovadas.join(', ')}`,
      caidas: [...o.capacidadesReprovadas],
    };
  }

  // 2b. A garantia que o restart automático existe para preservar: as
  // conversas que estavam vivas quando o daemon caiu têm de voltar sozinhas.
  //
  // **Aqui mora a distinção que separa "o lançamento quebrou" de "a máquina
  // estava sem memória".** Sem ela, uma máquina apertada reprovaria o canário
  // por um motivo que não diz nada sobre o código — um vermelho mentiroso, que
  // é o mesmo defeito dos instrumentos que dizem verde sem medir, ao contrário.
  //
  // Falta de memória vira `null` (não deu para medir), nunca vermelho: a guarda
  // `MEMORIA_MINIMA_MB` recusa abrir sessão, então a conversa não voltar é
  // consequência da máquina, não do lançamento. E `null` segura o portão do
  // mesmo jeito — só não acusa o inocente.
  if (o.sessoesQueVoltaram < o.sessoesQueDeviamVoltar) {
    const faltaram = o.sessoesQueDeviamVoltar - o.sessoesQueVoltaram;
    const semMemoria = o.memoriaLivreMb !== null && o.memoriaLivreMb < o.memoriaMinimaMb;

    if (semMemoria) {
      return {
        saude: null,
        sessoes,
        nota:
          `${faltaram} conversa(s) nao voltaram, mas a maquina estava com ` +
          `${o.memoriaLivreMb} MB livres (minimo ${o.memoriaMinimaMb}): ` +
          `nao da para dizer se foi o lancamento`,
      };
    }

    return {
      saude: 'VERMELHO_CAPACIDADE',
      sessoes,
      nota: `${faltaram} de ${o.sessoesQueDeviamVoltar} conversa(s) nao voltaram depois do restart`,
    };
  }

  // 3. O que não deu para medir. Depois da degradação, nunca antes.
  if (o.sessoesReabrindo > 0) {
    return {
      saude: null,
      sessoes,
      nota: `${o.sessoesReabrindo} sessao(oes) ainda reabrindo: medi cedo demais`,
    };
  }

  if (o.capacidadesReprovadas === null) {
    return { saude: null, sessoes, nota: 'a sonda nao rodou: as capacidades nao foram medidas' };
  }

  return { saude: 'VERDE', sessoes };
}

/** O veredito libera a frota? Só `VERDE`. `null` não libera — decisão nossa. */
export function liberaAFrota(v: Veredicto): boolean {
  return v.saude !== null && VEREDITOS_QUE_LIBERAM.includes(v.saude);
}
